import type { SupabaseClient } from '../../lib/supabase';
import {
  reactionErrorMessage,
  summarizeReactions,
  type ReactionSummary,
} from '../comments/reactions';

/**
 * Emoji reactions on the Console chat (My Day > Chat with Alex, and Alex's
 * private chat sheet on his link page). No second reaction system: rows live
 * in public.comment_reactions, chips are CommentReactions.tsx, the emoji are
 * the Hub's set (emojiSet.ts, QUICK_REACTIONS).
 *
 * Reads go through read_console_reactions for both sides
 * (20270242000100): without a token it is the signed-in Console owner, with
 * one it is the bearer link. The payload names no user id, only whether each
 * row is the reader's own, so a chip can say "You", the other person, or both.
 *
 * Writes: the owner writes comment_reactions directly under its own RLS
 * (addReaction / removeReaction in comments/reactions.ts). The link writes
 * through set_console_reaction, which only ever writes as Alex.
 */

interface ConsoleReactionRow {
  comment_id: string;
  emoji: string;
  mine: boolean;
  created_at: string;
}

/** How many message ids go into one read (the function refuses more than 200). */
export const CONSOLE_REACTION_CHUNK = 200;

function isRow(value: unknown): value is ConsoleReactionRow {
  if (value === null || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.comment_id === 'string' &&
    typeof row.emoji === 'string' &&
    typeof row.mine === 'boolean' &&
    typeof row.created_at === 'string'
  );
}

/** One chip per emoji per message, in first-use order, as everywhere else in the Hub. */
export function summarizeConsoleReactions(
  rows: ConsoleReactionRow[],
): Map<string, ReactionSummary[]> {
  return summarizeReactions(
    rows.map((row) => ({
      comment_id: row.comment_id,
      user_id: row.mine ? 'me' : 'them',
      emoji: row.emoji,
      created_at: row.created_at,
    })),
    'me',
  );
}

/**
 * Who reacted, in words. The Console chat is a DM between exactly two people,
 * so anyone who is not you is the other person.
 */
export function consoleReactionWho(reaction: ReactionSummary, partner: string): string {
  const others = reaction.count - (reaction.mine ? 1 : 0);
  const names: string[] = reaction.mine ? ['You'] : [];
  if (others === 1) names.push(partner);
  else if (others > 1) names.push(`${others} others`);
  return names.join(' and ');
}

/** Plain words for a refused reaction. Empty means nothing to say. */
export function consoleReactionError(raw: string): string {
  if (raw.includes('console_reaction_limit')) {
    return 'That message has as many reactions as it can take.';
  }
  return reactionErrorMessage(raw);
}

/**
 * Every reaction on these messages. Null when the chat cannot take reactions
 * (no live link, or not the owner): the caller then shows no reaction controls.
 */
export async function readConsoleReactions(
  client: SupabaseClient,
  commentIds: string[],
  token: string | null,
): Promise<Map<string, ReactionSummary[]> | null> {
  if (commentIds.length === 0) return new Map();
  const rows: ConsoleReactionRow[] = [];
  for (let at = 0; at < commentIds.length; at += CONSOLE_REACTION_CHUNK) {
    const { data, error } = await client.rpc('read_console_reactions', {
      p_comment_ids: commentIds.slice(at, at + CONSOLE_REACTION_CHUNK),
      ...(token === null ? {} : { p_token: token }),
    });
    if (error) throw new Error(error.message);
    if (data === null) return null;
    const list: unknown[] = Array.isArray(data) ? data : [];
    if (!Array.isArray(data) || !list.every(isRow)) throw new Error('reactions_unreadable');
    rows.push(...list.filter(isRow));
  }
  return summarizeConsoleReactions(rows);
}

/** Alex's link adds (on) or removes (off) his own reaction. */
export async function setConsoleReaction(
  client: SupabaseClient,
  token: string,
  commentId: string,
  emoji: string,
  on: boolean,
): Promise<void> {
  const { error } = await client.rpc('set_console_reaction', {
    p_token: token,
    p_comment_id: commentId,
    p_emoji: emoji,
    p_on: on,
  });
  if (error) throw new Error(error.message);
}
